import { collection, doc, getDoc, getDocs, query, runTransaction, where } from 'firebase/firestore/lite';
import { db } from '../firebaseConfig';
import { makeJourneyId } from '../storage/journeyStorage';
import { importFirestoreJourneyToLocal } from '../storage/localJourneyStore';

export const TEMPLATE_STATUS = 'published';

const requireString = (value, label, max) => {
  if (typeof value !== 'string' || !value.trim() || value.length > max) throw new Error(`Template ${label} is invalid.`);
  return value.trim();
};

export const validatePublishedTemplate = (id, data) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Template data is invalid.');
  if (data.status !== TEMPLATE_STATUS) throw new Error('Template is not published.');
  if (data.templateId !== id) throw new Error('Template identity is invalid.');
  if (!Number.isInteger(data.version) || data.version < 1) throw new Error('Template version is invalid.');
  if (!Array.isArray(data.steps) || data.steps.length > 100) throw new Error('Template steps are invalid.');
  let actionCount = 0;
  const steps = data.steps.map((step) => {
    if (!step || !Array.isArray(step.actionItems)) throw new Error('Template step is invalid.');
    actionCount += step.actionItems.length;
    return {
      id: requireString(step.id, 'step ID', 128),
      title: requireString(step.title, 'step title', 500),
      description: typeof step.description === 'string' ? step.description.slice(0, 10000) : '',
      actionItems: step.actionItems.map((item) => ({
        id: requireString(item?.id, 'action ID', 128),
        title: requireString(item?.title, 'action title', 500),
        description: typeof item?.description === 'string' ? item.description.slice(0, 10000) : '',
      })),
    };
  });
  if (actionCount > 500) throw new Error('Template has too many action items.');
  return {
    templateId: id,
    title: requireString(data.title, 'title', 120),
    description: requireString(data.description, 'description', 2000),
    category: requireString(data.category, 'category', 80),
    goal: requireString(data.goal, 'goal', 500),
    steps,
    version: data.version,
    status: data.status,
    publishedAt: data.publishedAt,
    updatedAt: data.updatedAt,
  };
};

export const listPublishedTemplates = async () => {
  const publishedQuery = query(collection(db, 'journeyTemplates'), where('status', '==', TEMPLATE_STATUS));
  const snapshot = await getDocs(publishedQuery);
  return snapshot.docs.map((item) => validatePublishedTemplate(item.id, item.data()));
};

export const loadPublishedTemplate = async (templateId) => {
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(templateId)) throw new Error('Template ID is invalid.');
  const snapshot = await getDoc(doc(db, 'journeyTemplates', templateId));
  if (!snapshot.exists()) return null;
  return validatePublishedTemplate(snapshot.id, snapshot.data());
};

const newJourneyId = (goal) => {
  const base = makeJourneyId(goal) || 'journey';
  const suffix = typeof window !== 'undefined' && window.crypto?.randomUUID
    ? window.crypto.randomUUID().slice(0, 8)
    : `${Date.now()}`;
  return `${base.slice(0, 110)}-${suffix}`;
};

export const copyPublishedTemplate = async (userId, templateId) => {
  if (!userId) throw new Error('Sign in before using a template.');
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(templateId)) throw new Error('Template ID is invalid.');
  const templateRef = doc(db, 'journeyTemplates', templateId);
  const journeyId = newJourneyId(templateId);
  const journeyRef = doc(db, 'users', userId, 'journeys', journeyId);
  const copied = await runTransaction(db, async (transaction) => {
    const templateSnapshot = await transaction.get(templateRef);
    if (!templateSnapshot.exists()) throw new Error('This template is no longer available.');
    const template = validatePublishedTemplate(templateSnapshot.id, templateSnapshot.data());
    const graph = {
      fromTheme: template.goal,
      startState: template.goal,
      context: '',
      endTheme: template.goal,
      endState: template.goal,
      steps: template.steps.map((step, stepIndex) => ({
        step_id: stepIndex,
        objective: step.title,
        objectiveDescription: step.description,
        items: step.actionItems.map((item) => ({
          keyID: typeof window !== 'undefined' && window.crypto?.randomUUID
            ? window.crypto.randomUUID()
            : `${journeyId}-${stepIndex}-${item.id}`,
          value: [item.title, item.description].filter(Boolean).join('\n\n'),
          status: 'active',
          relevance: 'unknown',
          user_note: '',
        })),
      })),
    };
    const updatedAt = new Date().toISOString();
    transaction.set(journeyRef, {
      graph: JSON.stringify(graph),
      shortname: template.title,
      ownerId: userId,
      journeyId,
      updatedAt,
    });
    return { journeyId, title: template.title, data: graph, updatedAt };
  });
  const journeyMeta = await importFirestoreJourneyToLocal({
    userId,
    journeyId: copied.journeyId,
    firestoreDoc: {
      graph: JSON.stringify(copied.data),
      shortname: copied.title,
      journeyId: copied.journeyId,
      updatedAt: copied.updatedAt,
    },
  });
  return { ...copied, data: journeyMeta.data, journeyMeta };
};
